import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { HttpStatusCode } from '../../shared/constants/http-status-code.enum';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../../shared/instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpPromise } from '../../shared/instrumentation/instrumented-http.service';
import { FileType } from '../../shared/models/file-item.model';
import { HttpRequestOptions } from '../../shared/models/http-request-options.model';
import { AccessTokenConsumer, AccessTokenService } from '../../shared/services/access-token.service';
import { OfficeHomeApiConfigService } from '../../shared/services/officehome-api-config.service';
import { SchedulerService } from '../../shared/services/scheduler.service';
import { XhrService, XhrServiceError } from '../../shared/services/xhr.service';
import { ContextService } from '../../shared/shared-context/context.service';
import { UserContextService } from '../../shared/shared-context/user-context.service';

export interface FeaturedDocument {
    Id: string;
    Title: string;
    Url: string;
    Thumbnail: string;
    Market: string;
}

export interface FeaturedDocuments {
    Documents: Array<FeaturedDocument>;
}

@Injectable({
    providedIn: 'root'
})
export class FeaturedService extends XhrService {
    private readonly fetchPromises: Map<FileType, Promise<Array<FeaturedDocument>>>;
    private static readonly cultureArg = 'culture=';
    private static readonly appArg = 'app=';
    private static readonly isEduArg = 'isEdu=';

    private readonly locale: string;
    private readonly requestConfig: HttpRequestOptions;

    constructor(
        accessTokenService: AccessTokenService,
        contextService: ContextService,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        platformLocation: PlatformLocation,
        private readonly userContextService: UserContextService,
        scheduler: SchedulerService
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp, AccessTokenConsumer.OfficeHome, contextService, officeHomeApiConfigService, platformLocation, scheduler);
        this.fetchPromises = new Map<FileType, Promise<Array<FeaturedDocument>>>();
        this.locale = contextService.sharedContext.officeMarket.toLowerCase();
        this.requestConfig = {
            headers: new Map()
        };
    }

    public getFeaturedDocuments(type: FileType): Promise<Array<FeaturedDocument>> {
        const appPromise = this.fetchPromises.get(type);
        if (appPromise) {
            return appPromise;
        }

        this.fetchPromises.set(type, new Promise<Array<FeaturedDocument>>((resolve, reject) => {
            const url = `${this.contextService.sharedContext.officeHomeApiRoot}/api/featured?${FeaturedService.appArg}${FileType[type]}&${FeaturedService.cultureArg}${this.locale}&${FeaturedService.isEduArg}${this.userContextService.user.isEdu}`;

            const finalErrorCallback = error => {
                // clear the promise so retry is possible.
                this.fetchPromises.delete(type);
                // reject to free any waiting callers
                reject(error);
            };

            const promiseCallback = (promise: InstrumentedHttpPromise<FeaturedDocuments>) => {
                promise
                    .instrument((result, event) => {
                        if (result.ok) {
                            if (result.data && result.data.Documents && result.data.Documents) {
                                const itemsCount = result.data.Documents.length;
                                event.params[InstrumentationParam.ActualSize] = itemsCount.toString();
                                event.params[InstrumentationParam.ItemCategory] = FileType[type];
                            } else {
                                event.params[InstrumentationParam.HttpCode] = HttpStatusCode.UnexpectedResponse.toString();
                            }
                        }
                    })
                    .then(({ data }) => {
                        if (!data || !data.Documents || !data.Documents) {
                            finalErrorCallback(new XhrServiceError(null, 'null featured documents response'));
                            return;
                        }

                        resolve(data.Documents);
                    })
                    .catch(error => {
                        this.globalErrorHandler.handleError(error, ErrorId.VnZwX);
                    });
            };

            this.get<FeaturedDocuments>(
                KnownInstrumentationEvents.FeaturedDocumentsRequest,
                url,
                promiseCallback,
                finalErrorCallback,
                this.requestConfig,
                FetchStrategy.NetworkOnly);
        }));

        return this.fetchPromises.get(type);
    }

    protected handleQueryParam(paramNameInLowerCase: string, value: string | Array<string>) {
        super.handleQueryParam(paramNameInLowerCase, value);
        if (paramNameInLowerCase === 'mockfeatured' && this.mockRequestHeader) {
            this.requestConfig.headers.set(this.mockRequestHeader, value);
        }
    }
}
